import { type ComponentType, type LazyExoticComponent, lazy } from 'react';

type LazyComponentModule<TProps extends object> = {
  readonly default: ComponentType<TProps>;
};

type VitePreloadErrorEvent = Event & {
  readonly payload?: unknown;
};

const RELOAD_STORAGE_KEY = 'jobtracker:dynamic-import-reload-attempted';
const RELOAD_RETRY_WINDOW_MS = 60_000;

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /module script load failed/i,
  /loading chunk \d+ failed/i,
  /chunkloaderror/i,
  /load failed/i,
];

const getErrorText = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }

  if (typeof error === 'object' && error !== null && 'payload' in error) {
    return getErrorText((error as VitePreloadErrorEvent).payload);
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { readonly message?: unknown }).message;

    if (typeof message === 'string') {
      return message;
    }
  }

  return String(error);
};

const getSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const hasRecentReloadAttempt = (storage: Storage): boolean => {
  const attemptedAt = Number(storage.getItem(RELOAD_STORAGE_KEY));

  return Number.isFinite(attemptedAt) && Date.now() - attemptedAt < RELOAD_RETRY_WINDOW_MS;
};

export const isDynamicImportError = (error: unknown): boolean => {
  const errorText = getErrorText(error);

  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => pattern.test(errorText));
};

export const reloadPageOnceForDynamicImportError = (error: unknown): boolean => {
  if (!isDynamicImportError(error) || typeof window === 'undefined') {
    return false;
  }

  if (window.navigator.onLine === false) {
    return false;
  }

  const storage = getSessionStorage();

  if (!storage || hasRecentReloadAttempt(storage)) {
    return false;
  }

  storage.setItem(RELOAD_STORAGE_KEY, String(Date.now()));
  window.location.reload();

  return true;
};

export const lazyWithReload = <TProps extends object>(
  importer: () => Promise<LazyComponentModule<TProps>>,
): LazyExoticComponent<ComponentType<TProps>> => {
  return lazy(async () => {
    try {
      const module = await importer();

      return module;
    } catch (error) {
      if (reloadPageOnceForDynamicImportError(error)) {
        return new Promise<LazyComponentModule<TProps>>(() => undefined);
      }

      throw error;
    }
  });
};

export const installDynamicImportReloadHandler = (): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handlePreloadError = (event: Event) => {
    const preloadErrorEvent = event as VitePreloadErrorEvent;

    if (reloadPageOnceForDynamicImportError(preloadErrorEvent.payload ?? event)) {
      event.preventDefault();
    }
  };

  window.addEventListener('vite:preloadError', handlePreloadError);

  return () => window.removeEventListener('vite:preloadError', handlePreloadError);
};
